import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldIcon, SettingsIcon, ZapIcon, BrainIcon, LockIcon, MessageCircleIcon } from 'lucide-react';
type Message = {
  id: string;
  sender: 'bot' | 'user';
  text: string;
  showUnlock?: boolean;
  showPrivacy?: boolean;
};
export function AIConsultant() {
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    sender: 'bot',
    text: "Welcome to QUBIX. I'm here to help you imagine what's possible in your home. Let's start simple — how large is your space?"
  }]);
  const [step, setStep] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [userName, setUserName] = useState('');
  const [killerResponse, setKillerResponse] = useState<'look' | 'respond'>('respond');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping]);
  useEffect(() => {
    if (inputRef.current && [6, 7, 8].includes(step)) {
      inputRef.current.focus();
    }
  }, [step]);
  const addBotMessage = (text: string, extras?: Partial<Message>) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      sender: 'bot',
      text,
      ...extras
    }]);
  };
  const addUserMessage = (text: string) => {
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      sender: 'user',
      text
    }]);
  };
  const simulateTyping = (callback: () => void, delay = 1500) => {
    setStep(0);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      callback();
    }, delay);
  };
  const handleOptionClick = (text: string, nextStep: number, variant?: 'look' | 'respond') => {
    addUserMessage(text);
    simulateTyping(() => {
      if (nextStep === 2) {
        addBotMessage('Lovely. A space of that size offers incredible possibilities. How many key rooms are you thinking of transforming?');
        setStep(2);
      } else if (nextStep === 3) {
        addBotMessage("Got it. Now tell me — what matters most to you in these spaces? What's your lifestyle like?");
        setStep(3);
      } else if (nextStep === 4) {
        addBotMessage("I love that. One more question — and this one's important. Do you want your home to just look good… or actually respond to you?");
        setStep(4);
      } else if (nextStep === 5) {
        if (variant) setKillerResponse(variant);
        if (variant === 'look') {
          addBotMessage("Beautiful taste. Based on everything you've shared, we'd recommend the Premium Tier — stunning audio, architectural lighting, and elegant control. Estimated investment: ₹50L – ₹1.5Cr.");
        } else {
          addBotMessage("Now we're talking. You've unlocked the full QUBIX experience. We recommend the Signature Tier — AI-driven automation, security integration, scene intelligence, and a home that truly anticipates you. Estimated investment: ₹2Cr – ₹5Cr+.", {
            showUnlock: true
          });
        }
        setStep(5);
      }
    });
  };
  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    const value = inputValue.trim();
    setInputValue('');
    if (step === 6) {
      // Name capture
      setUserName(value.split(' ')[0]);
      addUserMessage(value);
      simulateTyping(() => {
        addBotMessage(`Great to meet you, ${value.split(' ')[0]}. I've put together a personalized recommendation based on our conversation. Where should I send it? Just drop your email below.`);
        setStep(7);
      });
    } else if (step === 7) {
      // Email capture
      addUserMessage(value);
      simulateTyping(() => {
        addBotMessage(`Perfect, ${userName}. One last thing — in case our design team has a quick question about your space, what's the best number to reach you?`);
        setStep(8);
      });
    } else if (step === 8) {
      // Phone capture
      addUserMessage(value);
      simulateTyping(() => {
        addBotMessage(`Thank you, ${userName}. How would you prefer we reach out? We respect your time completely — no surprise calls, ever.`, {
          showPrivacy: true
        });
        setStep(9);
      }, 1800);
    }
  };
  const handleCommPref = (pref: string) => {
    addUserMessage(pref);
    simulateTyping(() => {
      addBotMessage(`You're all set, ${userName}. Your personalized recommendation is on its way. Our design team will reach out via ${pref.toLowerCase()} — on your terms, at your pace. Meanwhile, feel free to keep exploring.`);
      setStep(10);
    }, 2000);
  };
  const handleSaveChoice = (save: boolean) => {
    if (save) {
      addUserMessage('Yes, save my recommendation');
      simulateTyping(() => {
        addBotMessage('Wonderful. What should I call you? Just your first name is perfect.');
        setStep(6);
      });
    } else {
      addUserMessage("I'll keep exploring for now");
      simulateTyping(() => {
        addBotMessage("Of course. Everything you've seen is just the beginning — scroll down to explore further. We're here whenever you're ready.");
        setStep(11);
      });
    }
  };
  const borderColors = ['border-cinema', 'border-music', 'border-social', 'border-relax'];
  const inputPlaceholders: Record<number, string> = {
    6: 'Your first name...',
    7: 'your@email.com',
    8: '+91 98XXX XXXXX'
  };
  const inputTypes: Record<number, string> = {
    6: 'text',
    7: 'email',
    8: 'tel'
  };
  return <section id="intelligence" className="py-32 bg-obsidian relative" data-id="element-19">
      <div className="max-w-4xl mx-auto px-6" data-id="element-20">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-21">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-22">
            Your AI Home Consultant
          </h2>
          <p className="text-silver" data-id="element-23">
            Tell us about your lifestyle. We will design your experience.
          </p>
        </motion.div>

        {/* iPad Mockup Container */}
        <div className="bg-graphite p-2 md:p-4 rounded-[2rem] md:rounded-[3rem] shadow-[0_0_50px_rgba(99,102,241,0.1)] border border-steel relative" data-id="element-24">
          <div className="bg-void rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden h-[600px] flex flex-col relative border border-carbon" data-id="element-25">
            {/* Header */}
            <div className="bg-carbon/80 backdrop-blur-md border-b border-graphite p-4 flex items-center justify-center z-10" data-id="element-26">
              <span className="font-serif text-gold tracking-widest uppercase text-sm" data-id="element-27">
                QUBIX Intelligence
              </span>
            </div>

            {/* Chat Area */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6 scroll-smooth" data-id="element-28">
              <AnimatePresence initial={false} data-id="element-29">
                {messages.map(msg => <motion.div key={msg.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`} data-id="element-30">
                    <div className={`max-w-[85%] p-4 rounded-2xl ${msg.sender === 'user' ? 'bg-gradient-to-r from-cinema to-music text-white rounded-tr-sm' : 'bg-carbon text-platinum border-l-2 border-cinema rounded-tl-sm'}`} style={msg.sender === 'bot' ? {
                  borderImage: 'linear-gradient(to bottom, #6366F1, #EC4899) 1 100%'
                } : {}} data-id="element-31">
                      <p className="text-sm md:text-base leading-relaxed" data-id="element-32">
                        {msg.text}
                      </p>
                    </div>

                    {/* Visual Unlock */}
                    {msg.showUnlock && <motion.div initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} transition={{
                  delay: 0.5
                }} className="flex gap-4 mt-4 ml-4" data-id="element-33">
                        {[{
                    icon: SettingsIcon,
                    label: 'Automation',
                    color: '#10B981'
                  }, {
                    icon: ShieldIcon,
                    label: 'Security',
                    color: '#64748B'
                  }, {
                    icon: ZapIcon,
                    label: 'Scenes',
                    color: '#F59E0B'
                  }, {
                    icon: BrainIcon,
                    label: 'AI',
                    color: '#6366F1'
                  }].map((item, i) => <div key={i} className="flex flex-col items-center" data-id="element-34">
                            <div className="w-10 h-10 rounded-full bg-carbon border border-graphite flex items-center justify-center mb-2" style={{
                      boxShadow: `0 0 15px ${item.color}40`
                    }} data-id="element-35">
                              <item.icon className="w-5 h-5" style={{
                        color: item.color
                      }} data-id="element-36" />
                            </div>
                            <span className="text-[10px] text-silver uppercase tracking-wider" data-id="element-37">
                              {item.label}
                            </span>
                          </div>)}
                      </motion.div>}

                    {/* Privacy Reassurance */}
                    {msg.showPrivacy && <motion.div initial={{
                  opacity: 0,
                  y: 8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.6
                }} className="mt-4 ml-4 bg-carbon/60 border border-graphite rounded-xl p-4 max-w-[85%]" data-id="element-38">
                        <div className="flex items-start gap-3" data-id="element-39">
                          <LockIcon className="w-4 h-4 text-relax shrink-0 mt-0.5" data-id="element-40" />
                          <div data-id="element-41">
                            <p className="text-xs text-platinum mb-2 font-medium" data-id="element-42">
                              Your privacy matters to us
                            </p>
                            <p className="text-[11px] text-silver leading-relaxed" data-id="element-43">
                              We will never call without your consent. Our team
                              will message you first — personally, over
                              WhatsApp. Take your time, and only then decide if
                              you'd like to talk. We're equally comfortable on
                              email, Zoom, or Teams. Whatever works for you.
                            </p>
                          </div>
                        </div>
                      </motion.div>}
                  </motion.div>)}

                {isTyping && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="flex justify-start" data-id="element-44">
                    <div className="bg-carbon p-4 rounded-2xl rounded-tl-sm border-l-2 border-cinema flex space-x-2" style={{
                  borderImage: 'linear-gradient(to bottom, #6366F1, #EC4899) 1 100%'
                }} data-id="element-45">
                      <motion.div animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 0.6,
                    delay: 0
                  }} className="w-2 h-2 bg-cinema rounded-full" data-id="element-46" />
                      <motion.div animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 0.6,
                    delay: 0.2
                  }} className="w-2 h-2 bg-music rounded-full" data-id="element-47" />
                      <motion.div animate={{
                    y: [0, -5, 0]
                  }} transition={{
                    repeat: Infinity,
                    duration: 0.6,
                    delay: 0.4
                  }} className="w-2 h-2 bg-relax rounded-full" data-id="element-48" />
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Options / Input Area */}
            <div className="p-4 bg-carbon/50 border-t border-graphite min-h-[100px] flex items-center justify-center" data-id="element-49">
              <AnimatePresence mode="wait" data-id="element-50">
                {/* Step 1: Home Size */}
                {step === 1 && <motion.div key="s1" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-wrap justify-center gap-3" data-id="element-51">
                    {['Under 4,000 sq ft', '4,000 – 8,000 sq ft', '8,000+ sq ft'].map((opt, i) => <button key={opt} onClick={() => handleOptionClick(opt, 2)} className={`px-5 py-2 rounded-full border ${borderColors[i % 4]} text-platinum hover:bg-white/5 transition-colors text-sm`} data-id="element-52">
                        {opt}
                      </button>)}
                  </motion.div>}

                {/* Step 2: Rooms */}
                {step === 2 && <motion.div key="s2" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-wrap justify-center gap-3" data-id="element-53">
                    {['2–3 rooms', '4–6 rooms', 'Entire home'].map((opt, i) => <button key={opt} onClick={() => handleOptionClick(opt, 3)} className={`px-5 py-2 rounded-full border ${borderColors[i % 4]} text-platinum hover:bg-white/5 transition-colors text-sm`} data-id="element-54">
                        {opt}
                      </button>)}
                  </motion.div>}

                {/* Step 3: Lifestyle */}
                {step === 3 && <motion.div key="s3" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-wrap justify-center gap-3" data-id="element-55">
                    {['Entertainment focused', 'Wellness & calm', 'Social & hosting', 'All of the above'].map((opt, i) => <button key={opt} onClick={() => handleOptionClick(opt, 4)} className={`px-5 py-2 rounded-full border ${borderColors[i % 4]} text-platinum hover:bg-white/5 transition-colors text-sm`} data-id="element-56">
                        {opt}
                      </button>)}
                  </motion.div>}

                {/* Step 4: Killer Question */}
                {step === 4 && <motion.div key="s4" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-col md:flex-row justify-center gap-4 w-full px-4" data-id="element-57">
                    <button onClick={() => handleOptionClick('Just look good', 5, 'look')} className="px-6 py-3 rounded-full border border-graphite text-silver hover:border-silver transition-colors text-sm flex-1" data-id="element-58">
                      Just look good
                    </button>
                    <button onClick={() => handleOptionClick('Actually respond to me', 5, 'respond')} className="relative px-6 py-3 rounded-full text-white text-sm font-medium group overflow-hidden flex-1" data-id="element-59">
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cinema via-music to-social opacity-80 group-hover:opacity-100 transition-opacity duration-300" data-id="element-60"></span>
                      <span className="relative z-10" data-id="element-61">
                        Actually respond to me
                      </span>
                    </button>
                  </motion.div>}

                {/* Step 5: Save recommendation? */}
                {step === 5 && <motion.div key="s5" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-col items-center gap-3 w-full px-4" data-id="element-62">
                    <p className="text-silver text-xs mb-1" data-id="element-63">
                      This recommendation was crafted just for you.
                    </p>
                    <div className="flex flex-col md:flex-row justify-center gap-3 w-full" data-id="element-64">
                      <button onClick={() => handleSaveChoice(true)} className="relative px-6 py-3 rounded-full text-white text-sm font-medium group overflow-hidden flex-1" data-id="element-65">
                        <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cinema to-music opacity-80 group-hover:opacity-100 transition-opacity duration-300" data-id="element-66"></span>
                        <span className="relative z-10" data-id="element-67">
                          Save & send me the details
                        </span>
                      </button>
                      <button onClick={() => handleSaveChoice(false)} className="px-6 py-3 rounded-full border border-graphite text-silver hover:text-platinum transition-colors text-sm flex-1" data-id="element-68">
                        I'll keep exploring for now
                      </button>
                    </div>
                  </motion.div>}

                {/* Steps 6, 7, 8: Text Inputs */}
                {[6, 7, 8].includes(step) && <motion.form key={`input-${step}`} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} onSubmit={handleInputSubmit} className="flex w-full gap-3 px-2" data-id="element-69">
                    <input ref={inputRef} type={inputTypes[step] || 'text'} value={inputValue} onChange={e => setInputValue(e.target.value)} placeholder={inputPlaceholders[step] || ''} className="flex-1 bg-void border border-graphite rounded-full px-5 py-3 text-platinum text-sm focus:border-cinema focus:outline-none transition-colors placeholder:text-ash" data-id="element-70" />
                    <button type="submit" className="relative px-6 py-3 rounded-full text-white text-sm font-medium group overflow-hidden shrink-0" data-id="element-71">
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cinema to-music opacity-80 group-hover:opacity-100 transition-opacity duration-300" data-id="element-72"></span>
                      <span className="relative z-10" data-id="element-73">
                        {step === 8 ? 'Done' : 'Continue'}
                      </span>
                    </button>
                  </motion.form>}

                {/* Step 9: Communication Preference */}
                {step === 9 && <motion.div key="s9" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-col items-center gap-3 w-full px-4" data-id="element-74">
                    <div className="flex items-center gap-2 mb-1" data-id="element-75">
                      <MessageCircleIcon className="w-4 h-4 text-relax" data-id="element-76" />
                      <p className="text-silver text-xs" data-id="element-77">
                        How would you like us to connect?
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3" data-id="element-78">
                      {['WhatsApp', 'Email', 'Zoom / Teams', 'Phone call'].map((pref, i) => <button key={pref} onClick={() => handleCommPref(pref)} className={`px-5 py-2 rounded-full border ${borderColors[i % 4]} text-platinum hover:bg-white/5 transition-colors text-sm`} data-id="element-79">
                            {pref}
                          </button>)}
                    </div>
                  </motion.div>}

                {/* Step 10: Complete */}
                {step === 10 && <motion.div key="s10" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className="flex flex-wrap justify-center gap-3" data-id="element-80">
                    <a href="#consult" className="relative px-6 py-2 rounded-full text-white text-sm font-medium group overflow-hidden" data-id="element-81">
                      <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cinema to-music opacity-80 group-hover:opacity-100 transition-opacity duration-300" data-id="element-82"></span>
                      <span className="relative z-10" data-id="element-83">Book a Consultation</span>
                    </a>
                    <button onClick={() => setStep(11)} className="px-6 py-2 rounded-full border border-graphite text-silver hover:text-platinum transition-colors text-sm" data-id="element-84">
                      Keep Exploring
                    </button>
                  </motion.div>}

                {/* Step 11: Done */}
                {step === 11 && <motion.div key="s11" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-silver text-sm italic text-center" data-id="element-85">
                    {userName ? `Thank you, ${userName}. Scroll to explore further.` : "Scroll to explore further. We're here whenever you're ready."}
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}